import { ConsultationTokensType, ConsultationType } from '@/types/models/consultations';
import { makeGetRequest, makeFormRequest, makePostRequest, Response, OkResponse } from "@/api/helpers";
import { getData } from '@/api/crud';

import { generateMetainfo } from '@/utils/meta';

import { Consultations } from '@/models/consultations';

import { transformConsultationRequest } from './backend';

import {
    ConsultationStatus,
    Consultation,
    ChatAttachment,
    ConsultationTokens,
    ConsultationRequest
} from '@/types/consultations';
import { Message, CallMessage } from '@/types/consultations/chat';
import { Photo } from '@/types/common';

export const getConsultations = async () => {
    const result = await makeGetRequest(`api/consultations/?page=1`);
    
    return result.data?.result?.table?.rows || [] as ConsultationType[];
};

export const getConsultationCalculated = async (medicalServiceTypeId: number, doctorId: number, specializationId: number, patientId: number, clinicId?: number) => {
    const result = await makeGetRequest(
        `/api/consultations/calculate-price?medicalServiceTypeId=${medicalServiceTypeId}&doctorId=${doctorId}&specializationId=${specializationId}&patientId=${patientId}${clinicId ? '&clinicId=' + clinicId : ''}`
    );

    return result.data.result.price;
}

export const getConsultationTokens = async (id: number|string) => {
    const response = await makeGetRequest<Response<{ chat: ConsultationTokens }>>(`api/consultations/${id}/chat-tokens`);
    
    return response.data.result.chat as ConsultationTokensType;
};

export const setConsultationTokens = async (id: number|string, rtc_token: number|string) => {
    const response = await makePostRequest<Response<{ chat: ConsultationTokens }>>(`api/consultations/${id}/chat-tokens`, {rtc_token});

    return response.data.result.chat as ConsultationTokensType;
};

export const getConsultation = async (consultationId: string) => {
    try {
        const result = await makeGetRequest<Response<{ consultation: Consultation }>>(`api/consultations/${consultationId}`);

        return result.data.result.consultation;
    } catch (clientError: any) {
        const error = clientError.response.data

        throw new Error(error.message);
    }
};

export const getConsultationChatHistory = async (consultationId: string, page = 1, limit = 20) => {
    try {
        const result = await makeGetRequest<Response<Message[]>>(
            `api/chats/${consultationId}/messages?page=${page}&limit=${limit}`
        );

        return result.data.result?.reverse();
    } catch (clientError: any) {
        const error = clientError.response.data

        throw new Error(error.message);
    }
}

export const saveConsultationChatHistory = async (consultationId: string, messages: Message[]) => {
    try {
        await makePostRequest<OkResponse>(`api/chats/${consultationId}/messages`, {
            messages,
        });
    } catch (clientError: any) {
        const error = clientError.response.data

        throw new Error(error.message);
    }
}

export const logCallStatus = async (consultationId: string, text: string) => {
    const { id, timestamp } = generateMetainfo();

    const callLog: CallMessage = {
        id,
        datetimeStamp: timestamp,
        type: 'call',
        sender: 'doctor',
        body: text,
    }

    await saveConsultationChatHistory(consultationId, [callLog]);

    return callLog;
} 

export const uploadAttachment = async (consultationId: string, attachment: ChatAttachment) => {
    interface AttachmentResponse {
        attachments: [Photo],
        id: string,
    }

    try {
        const response = await makeFormRequest<Response<AttachmentResponse>>(`api/chats/${consultationId}/attachments`, {
           'files[]': attachment.file,
           'datetimeStamp': attachment.timestamp,
           'id': attachment.id,
        })

        const [photo] = response.data.result.attachments;

        return photo.src;
    } catch (error: any) {
        const message = error.response.data.message;

        throw new Error(message);
    }
}

export const updateConsultationStatus = async (consultationId: string, status: ConsultationStatus): Promise<string> => {
    const statusCommand = `go_to_${status}`;

    try {
        const result = await makeFormRequest(`api/consultations/${consultationId}/update/status`, {
           statusTransition: statusCommand,
        })

        return result.data.result.startedAt;
    } catch (error: any) {
        const message = error.response.data.message;

        throw new Error(message);
    }
}

export const getUnprocessedConsultations = async (): Promise<ConsultationRequest[]> => {
    const result = await getData(
        Consultations.moduleName,
        '/pending-list'
    );

    const requests = result.map(transformConsultationRequest);

    return requests;
}

export const confirmConsultationRequest = async (request: ConsultationRequest) => {
    if  (!request.doctor) {
        throw new Error('Не выбран врач')
    }

    if (!request.time) {
        throw new Error('Не выбрано время консультации');
    }

    const data: {
        doctorId: number,
        startDateTime: string,
        endDateTime: string,
        price?: string | number,
    } = {
        doctorId: request.doctor.id,
        startDateTime: request.time.start,
        endDateTime: request.time.end,
    }

    if (request.price && !isNaN(+request.price)) {
        data.price = +request.price;
    }

    try {
        await makeFormRequest(`api/consultations/${request.id}/confirm`, data);
    } catch (clientError: any) {
        const error = clientError.response.data;

        throw new Error(error.message)
    }
}
